import moment from 'moment';
import * as dateMath from 'date-arithmetic';

// 참고> 
// https://github.com/jquense/react-big-calendar/blob/master/src/utils/dates.js

export const START_OF_WEEK = 1;

/**
 * date가 속하는 month의 첫 째 날 
 * @param {*} date 
 */
export function firstDateOfMonth(date) {
	return dateMath.startOf(date, 'month');
}

/**
 * date가 속하는 week의 첫 째 날 
 * @param {*} date 
 */
export function firstDateOfWeek(date, startOfWeek = START_OF_WEEK) {
	return dateMath.startOf(date, 'week', startOfWeek);
}

/**
 * 첫 번 째 일요일 
 * @param {*} date 
 * @param {*} startOfWeek 
 */
export function firstVisibleDay(date, startOfWeek = START_OF_WEEK) {
	let firstOfMonth = dateMath.startOf(date, 'month');
	return dateMath.startOf(firstOfMonth, 'week', startOfWeek);
}

/**
 * 마지막 토요일 
 * @param {*} date 
 * @param {*} startOfWeek 
 */
export function lastVisibleDay(date, startOfWeek = START_OF_WEEK) {
	let endOfMonth = dateMath.endOf(date, 'month');
	return dateMath.endOf(endOfMonth, 'week', startOfWeek);
}

/**
 * 첫 째 일요일부터 마지막 토요일까지 
 * @param {*} date 
 * @param {*} startOfWeek 
 */
export function visibleDays(date, startOfWeek = START_OF_WEEK) {
	let current = firstVisibleDay(date, startOfWeek),
			last = lastVisibleDay(date, startOfWeek),
			days = [];

	while (dateMath.lte(current, last, 'day')) {
		days.push(current);
		current = dateMath.add(current, 1, 'day');
	}

	return days;
}

export function compareDates(a, b) {
	return dateMath.lt(a, b, 'day') ? -1 : 
		(dateMath.eq(a, b, 'day') ? 0 : 1);
}

/**
 * return days of a week including given date 
 * @param {*} date 
 * @param {*} startOfWeek 
 */
export function weekDays(date, startOfWeek = START_OF_WEEK) {
	let current = dateMath.startOf(date, 'week', startOfWeek),
		last = dateMath.endOf(date, 'week', startOfWeek),
		days = [];
	while (dateMath.lte(current, last, 'day')) {
		days.push(current);
		current = dateMath.add(current, 1, 'day');
	}

	return days;
}

export function isPastWeek(week, startOfWeek = START_OF_WEEK) {
	if (week.length == 0) return false;

	let tmpWeek = week;
	if (week[0].getDay() !== startOfWeek) {
		// startOfWeek 다른 경우 처리 
		const monday = week.find(d => d.getDay() === 1);
		tmpWeek = weekDays(monday, startOfWeek);
	}
	const firstDay = dateMath.startOf(new Date(), 'week', startOfWeek);
	return isGt(firstDay, tmpWeek[tmpWeek.length - 1]);
}

export function fromToday(dates) {
	const today = new Date();
	return dates.filter(d => isGte(d, today));
}

/**
 * 이전 달 같은 일 
 * @param {Date} date 
 */
export function getPrevMonthDay(date) {
	return dateMath.subtract(date, 1, 'month');
}

/**
 * 다음 달 같은 일 
 * @param {Date} date 
 */
export function getNextMonthDay(date) {
	return dateMath.add(date, 1, 'month');
}

/**
 * 이전 주 같은 일 
 * @param {Date} date 
 */
export function getPrevWeekDay(date) {
	return dateMath.subtract(date, 1, 'week');
}

/**
 * 다음 주 같은 일 
 * @param {Date} date 
 */
export function getNextWeekDay(date) {
	return dateMath.add(date, 1, 'week');
}

/** 
 * 같은 날인지 확인 
 */
export function isSameDay(dateA, dateB) {
	return dateMath.eq(dateA, dateB, 'day');
}

export function isLt(dateA, dateB) {
	return dateMath.lt(dateA, dateB, 'day');
}

export function isLte(dateA, dateB) {
	return dateMath.lte(dateA, dateB, 'day');
}

export function isGt(dateA, dateB) {
	return dateMath.gt(dateA, dateB, 'day');
}

export function isGte(dateA, dateB) {
	return dateMath.gte(dateA, dateB, 'day');
}

/**
 * YYYYMM
 */
export function getYearMonth(date) {
	return moment(date).format('YYYYMM');
}

/**
 * YYYYMMDD
 */
export function getYearMonthDate(date) {
	return moment(date).format('YYYYMMDD');
}

/**
 * YYYY.MM.DD
 */
export function getYearMonthDate2(date) {
	return moment(date).format('YYYY.MM.DD');
}

/**
 * YYYY-MM-DD
 */
export function getYearMonthDate3(date) {
	return moment(date).format('YYYY-MM-DD');
}

/**
 * YYYY[년] MM[월]
 */
export function getYearMonthKor(date) {
	return moment(date).format('YYYY[년] MM[월]');
}

/**
 * MM월 DD일 (Day)
 */
export function getMonthDateDayKor(date) {
	const text = moment(date).format('M월 D일 (ddd)');
	// return text.slice(0, -2);
	return text;
}

export function getDateTimeTextKor(date) {
	return moment(date).format('YYYY년 M월 D일 LT');
}

/**
 * 월화수목금 
 */
export function getDayText(date, withDayUnit = true) {
	const text = moment(date).format('dddd');
	return withDayUnit ? text : text.substring(0, text.length - 2);
}

/**
 * HHMM
 * @param {*} date 
 */
export function getTimeString(date) {
	return ('' + date.getHours()).padStart(2, 0) + 
	('' + date.getMinutes()).padStart(2, 0);
}

/**
 * convert 'YYYYMMDD' date string to Date instance
 * @param {string} dateString
 */
export function getDate(dateString) {
	//TODO check date format 

	return new Date(
		dateString.substring(0, 4) + '-' + 
		dateString.substring(4, 6) + '-' + 
		dateString.substring(6) + 'T00:00:00+09:00'
	);
}

/**
 * convert 'YYYYMMDD' to 'YYYY-MM-DD'
 * @param {*} dateString 
 */
export function addDashToDateString(dateString) {
	return `${dateString.substring(0, 4)}-${dateString.substring(4, 6)}-${dateString.substring(6)}`;

}

/**
 * 날짜 string 과 시간 string 조합 
 * @param {*} dateString 
 * @param {*} timeString 
 */
export function getDateWithTime(dateString, timeString) {
	return new Date(
		addDashToDateString(dateString) + 
		'T' + timeString.substring(0, 2) + ':' + timeString.substring(2) + 
		':00+09:00'
	);
}


export function weekNumberByMonth(dateFormat) {
	const inputDate = new Date(dateFormat);
   
	// 인풋의 년, 월
	let year = inputDate.getFullYear();
	let month = inputDate.getMonth() + 1;
   
	// 목요일 기준 주차 구하기
	const weekNumberByThurFnc = (paramDate) => {
   
	  const year = paramDate.getFullYear();
	  const month = paramDate.getMonth();
	  const date = paramDate.getDate();
   
	  // 인풋한 달의 첫 날과 마지막 날의 요일
	  const firstDate = new Date(year, month, 1);
	  const lastDate = new Date(year, month+1, 0);
	  const firstDayOfWeek = firstDate.getDay() === 0 ? 7 : firstDate.getDay();
	  const lastDayOfweek = lastDate.getDay();
   
	  // 인풋한 달의 마지막 일
	  const lastDay = lastDate.getDate();
   
	  // 첫 날의 요일이 금, 토, 일요일 이라면 true
	  const firstWeekCheck = firstDayOfWeek === 5 || firstDayOfWeek === 6 || firstDayOfWeek === 7;
	  // 마지막 날의 요일이 월, 화, 수라면 true
	  const lastWeekCheck = lastDayOfweek === 1 || lastDayOfweek === 2 || lastDayOfweek === 3;
   
	  // 해당 달이 총 몇주까지 있는지
	  const lastWeekNo = Math.ceil((firstDayOfWeek - 1 + lastDay) / 7);
   
	  // 날짜 기준으로 몇주차 인지
	  let weekNo = Math.ceil((firstDayOfWeek - 1 + date) / 7);
   
	  // 인풋한 날짜가 첫 주에 있고 첫 날이 월, 화, 수로 시작한다면 'prev'(전달 마지막 주)
	  if(weekNo === 1 && firstWeekCheck) weekNo = 'prev';
	  // 인풋한 날짜가 마지막 주에 있고 마지막 날이 월, 화, 수로 끝난다면 'next'(다음달 첫 주)
	  else if(weekNo === lastWeekNo && lastWeekCheck) weekNo = 'next';
	  // 인풋한 날짜의 첫 주는 아니지만 첫날이 월, 화 수로 시작하면 -1;
	  else if(firstWeekCheck) weekNo = weekNo -1;
   
	  return weekNo;
	};
   
	// 목요일 기준의 주차
	let weekNo = weekNumberByThurFnc(inputDate);
   
	// 이전달의 마지막 주차일 떄
	if(weekNo === 'prev') {
	  // 이전 달의 마지막날
	  const afterDate = new Date(year, month-1, 0);
	  year = month === 1 ? year - 1 : year;
	  month = month === 1 ? 12 : month - 1;
	  weekNo = weekNumberByThurFnc(afterDate);
	}
	// 다음달의 첫 주차일 때
	if(weekNo === 'next') {
	  year = month === 12 ? year + 1 : year;
	  month = month === 12 ? 1 : month + 1;
	  weekNo = 1;
	}
   
	return {year, month, weekNo};
}

/**
 * 
 * @param {*} birthYearStr 2-digit birth year string 
 */
export function getAge(birthYearStr) {
	// 1940 이전 출생자는 가입 확률 없다고 판단. 
	const birthYear = parseInt(birthYearStr);
	let fullYear = birthYear < 40 ? 2000 + birthYear : 1900 + birthYear;
	let currYear = new Date().getFullYear() - fullYear + 1;
	return currYear;
}